// PARTNER-PORTÁL · KOMMENT-ÍRÁS a jegy-szál alján — RAGADÓS sávban, rich-text szerkesztővel.
//
// MIÉRT RAGADÓS (user-kérés): egy 50+ kommentes szálon a lap aljára tett beviteli mezőhöz végig kell
// görgetni, hogy a felhasználó egyáltalán megtalálja — ugyanaz a lelet, ami a belső szálon is felmerült, és
// ugyanaz a megoldás (`ticket-comments.tsx` `STICKY_BAR`): a sáv a szál ALJÁN ül, átlátszatlan háttérrel, és
// a görgetés alatt a helyén marad. Ez az Asana/Linear/GitHub minta.
//
// MIÉRT ÖSSZECSUKOTT ALAPÁLLAPOT: kinyitva a teljes toolbar + a szerkesztő ~180px-t vesz el a szálból, holott
// a felhasználó legtöbbször OLVAS. Egy sor magas sávra kattintva nyílik ki, és a kurzor egyből a mezőben van
// (`autoFocus`) — nem kell másodszor kattintani.
//
// MIÉRT RICH-TEXT (user-kérés): a partner ugyanazt a tárolt HTML-t írja, amit a belső kolléga olvas, és
// fordítva. Korábban itt sima `Textarea` állt, tehát a partner nem tudott képet/felvételt beszúrni oda,
// ahol épp a hibát mutatná — a kép-utak viszont a PARTNER végpontjaira injektálva mennek (a cél-fiók
// kötelező, és a cél-ügyfél a hívó portál-hozzáféréséből oldódik fel).
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DraftStatus } from '@/components/shared/draft-status';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Text } from '@/components/ui/text';
import {
  PORTAL_NEW_TICKET_IMAGE_RESOLVER,
  uploadPortalEditorImage,
} from '@/features/portal/editor-images';
import { isRichTextBlank } from '@/lib/sanitize-html';
import { cn } from '@/lib/utils';

/**
 * A RAGADÓS SÁV. NINCS `relative` a hajszálvonal mellett: a `cn` (tailwind-merge) a `sticky`-t és a
 * `relative`-ot UGYANABBA a pozíció-csoportba teszi, és az UTOLSÓT tartja meg — a `relative` tehát KIÜTNÉ a
 * `sticky`-t, és a sáv csendben visszakerülne a szál aljára (mérve a belső szálon). Nem is kell: a `sticky`
 * elem MAGA is containing block az `absolute` leszármazottnak.
 *
 * A FELSŐ ELVÁLASZTÓ VONAL BEHÚZOTT (`before:inset-x-6`), nem szélig futó `border-t` — a rendszer
 * hajszálvonal-idiómája ez (`DataTable`, `CardRows`, és a belső komment-sáv is így áll).
 */
const STICKY_BAR = cn(
  // `rounded-b-xl`: a hordozó kártya `overflow-visible` (a sticky miatt), tehát a sáv MAGA adja a kártya
  // alsó lekerekítését — enélkül a két alsó sarok elszögletesedne.
  'bg-card sticky bottom-0 z-10 rounded-b-xl px-4 py-2.5',
  "before:pointer-events-none before:absolute before:inset-x-6 before:top-0 before:h-px before:bg-[color:var(--table-hairline)] before:content-['']",
);

export interface PortalTicketComposerProps {
  /** A cél-fiók — a beszúrt kép/videó CÉL-FIÓKJA (a tenant ebből oldódik fel a szerveren). */
  accountId: string;
  value: string;
  onChange: (html: string) => void;
  onSubmit: () => void;
  pending: boolean;
  /** Az automatikusan mentett vázlat ideje (a jelzéshez) — a mentést a hívó végzi (`useDraft`). */
  draftSavedAt?: Date | null;
}

export function PortalTicketComposer({
  accountId,
  value,
  onChange,
  onSubmit,
  pending,
  draftSavedAt = null,
}: PortalTicketComposerProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // ÜRES-e a törzs: a LÁTHATÓ szöveg hosszán mérünk, nem a markupon — egy `<p></p>` padding nem tartalom.
  // a csak képből álló válasz NEM üres (közös szabály: `isRichTextBlank`)
  const blank = isRichTextBlank(value);

  if (!open) {
    return (
      <div className={STICKY_BAR}>
        {/* EGYSOROS, kattintható sáv — a kinyitott szerkesztő HELYÉT jelzi, nem egy második beviteli mező.
            `button`, nem `div`: billentyűzetről is elérhető, és a képernyőolvasó műveletként mondja. */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="border-border bg-muted/40 text-muted-foreground hover:border-brand hover:text-foreground ease-atlas flex w-full items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors duration-150"
        >
          <PaperAirplaneIcon className="size-4 shrink-0" />
          <span className="truncate">
            {blank ? t('portal.tickets.commentCollapsed') : t('drafts.resume')}
          </span>
        </button>
      </div>
    );
  }

  return (
    <form
      className={STICKY_BAR}
      onSubmit={(e) => {
        e.preventDefault();
        if (blank || pending) return;
        onSubmit();
      }}
    >
      <RichTextEditor
        value={value}
        onChange={onChange}
        placeholder={t('portal.tickets.commentPlaceholder')}
        aria-label={t('portal.tickets.commentLabel')}
        disabled={pending}
        autoFocus
        // A KÉP-/VIDEÓ-UTAK a PARTNER végpontjaira: a cél-fiók kötelező, és a cél-ügyfél a hívó
        // portál-hozzáféréséből oldódik fel — a belső „fiók nélkül a MORGENS ügyfél a gazda" fallback itt
        // elvi hiba lenne. A feloldó a most feltöltött médiát a commit `displayUrl`-jéből szolgálja ki.
        imageAccountId={accountId}
        uploadImage={(file) => uploadPortalEditorImage(file, accountId)}
        imageResolver={PORTAL_NEW_TICKET_IMAGE_RESOLVER}
      />
      <div className="mt-2.5 flex items-center justify-end gap-2">
        {/* a „Mégse" a vázlatot is elveti (üres szöveg = nincs vázlat) — külön elvetés-gomb ezért nem kell */}
        <DraftStatus savedAt={draftSavedAt} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={pending}
          onClick={() => {
            onChange('');
            setOpen(false);
          }}
        >
          {t('portal.tickets.cancel')}
        </Button>
        <Button type="submit" variant="brand" size="sm" disabled={blank || pending}>
          <PaperAirplaneIcon />
          {pending ? t('portal.tickets.sending') : t('portal.tickets.commentSend')}
        </Button>
      </div>
      {/* A MIN-HOSSZ SZABÁLY itt NEM érvényes (az a jegy-NYITÁS leírására szól) — egy komment lehet rövid. */}
      <Text variant="secondary" as="p" className="mt-1.5 text-[11.5px]">
        {t('portal.tickets.commentHint')}
      </Text>
    </form>
  );
}
